// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// API servisi
import { createContactEmailAPI, getContactEmailAPI, getContactEmailsAPI, updateContactEmailAPI } from "services/api/contacts/api";
import { ContactEmailEditFormValues, ContactEmailRow } from "services/api/contacts/types";
import { initialPagination } from "ui/components/Table/consts";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialContactEmailEditForm } from "services/api/contacts/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";

// Tipovi i konstante
import { ContactEmailEditProps } from "./types";
import { CONTACT_EMAIL_EDIT_FORM_SCHEMA } from "./validation";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { CheckBox } from "ui/components/CheckBox";
import { TextArea } from "ui/components/TextField/TextArea";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";


const ContactEmailEditCard: React.FC<ContactEmailEditProps> = (props) => {
  const { emailId, contactId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingEmails, setExistingEmails] = useState<ContactEmailRow[]>([]);

  const [formValues, setFormValues] =
    useState<ContactEmailEditFormValues>(initialContactEmailEditForm);

  useEffect(() => {
    if (!show) return;
    loadForm();
  }, [emailId, show]);

  const loadForm = async () => {
    const emails = await fetchExistingEmails();
    fetchEmail(emails);
  };

  const fetchExistingEmails = async (): Promise<ContactEmailRow[]> => {
    try {
      const result = await getContactEmailsAPI({ ...initialPagination, pageSize: 100 }, contactId);

      setExistingEmails(result.data);
      return result.data;
    } catch {
      setExistingEmails([]);
      return [];
    }
  };

  const fetchEmail = async (emails: ContactEmailRow[]) => {
    if (!emailId) {
      // Prvi email kontakta je odmah primarni.
      setFormValues({ ...initialContactEmailEditForm, primary: emails.length === 0 });
      setErrors({});
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactEmailAPI(contactId, emailId);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CONTACT_EMAIL_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    
   
    // Provjeri da li email već postoji na kontaktu (isključi trenutni email ako uređujemo)
    const emailExists = existingEmails.some(
      (email) => email.emailAddress.toLowerCase() === formValues.emailAddress?.toLowerCase() && email.id !== emailId
    );

     console.log('emailExists',emailExists)

    if (emailExists) {
      setErrors((prev) => ({
        ...prev,
        emailAddress: t("admin.contact.email.validation.exists"),
      }));
      return;
    }

    // Provjeri da li već postoji primarni email (isključi trenutni email ako uređujemo)
    if (formValues.primary) {
      const existingPrimaryEmail = existingEmails.find(
        (email) => email.primary && email.id !== emailId
      );

      if (existingPrimaryEmail) {
        setErrors((prev) => ({
          ...prev,
          primary: `${t("admin.contact.email.validation.primaryexists")}: ${existingPrimaryEmail.emailAddress}`,
        }));
        return;
      }
    }

    console.log('errors',errors)

    setIsLoading(true);

    try {
      let result;
      if (!!emailId) {
        result = await updateContactEmailAPI(contactId, emailId, formValues);
      } else {
        result = await createContactEmailAPI(contactId, formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, primary: !old.primary }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    })
  }




  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={emailId ? t("admin.contact.email.edit.title") : t("admin.contact.email.add.title")}
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="">
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextField
                        type="text"
                        name="emailAddress"
                        label={t("admin.contact.add.emailaddress")}
                        placeholder={t("admin.contact.add.emailaddress.placeholder")}
                        onChange={handleTextFieldChange}
                        error={errors.emailAddress}
                        value={formValues.emailAddress}
                        required
                        forceShowError
                        endIcon=""
                      ></TextField>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextArea
                        name="description"
                        label={t("admin.contact.add.description")}
                        placeholder={t("admin.contact.add.description.placeholder")}
                        onChange={handleTextFieldChange}
                        error={errors.description}
                        value={formValues.description}
                        required
                        endIcon=""
                      ></TextArea>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <CheckBox
                        name="primary"
                        onChange={handleCheckBoxChange}
                        checked={Boolean(formValues.primary)}
                        label={t("admin.contact.add.primary")}
                        error={errors.primary}
                      ></CheckBox>
                    </div>
                  </div>
                </div>

              </form>
            </div>
          </div>
        </div>
        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button
            variant="outlined"
            children={t("admin.close")}
            onClick={handleCloseModal}
          />
          <Button
            variant="primary"
            children={t("admin.save")}
            onClick={handleFormSubmit}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};



export default memo(ContactEmailEditCard);
